<template>
  <div class="course-management">
    <header class="page-header">
      <div>
        <h2>课程管理</h2>
        <p class="subtitle">维护课程信息、控制报名状态与名额</p>
      </div>
      <button class="primary-button" @click="openCreateForm">新建课程</button>
    </header>

    <section class="filter-panel">
      <div class="filter-item">
        <label>关键字</label>
        <input v-model="filters.keyword" placeholder="标题 / 讲师" />
      </div>
      <div class="filter-item">
        <label>状态</label>
        <select v-model="filters.status">
          <option value="">全部</option>
          <option v-for="option in statusOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>
      <div class="filter-item">
        <label>开课时间</label>
        <input type="date" v-model="filters.startDate" />
      </div>
      <div class="filter-item">
        <label>结课时间</label>
        <input type="date" v-model="filters.endDate" />
      </div>
      <div class="filter-actions">
        <button class="primary-button" @click="loadCourses">查询</button>
        <button class="ghost-button" @click="resetFilters">重置</button>
      </div>
    </section>

    <section class="table-wrapper" v-if="courses.length">
      <table>
        <thead>
          <tr>
            <th>课程名称</th>
            <th>讲师</th>
            <th>标签</th>
            <th>时间</th>
            <th>名额</th>
            <th>价格</th>
            <th>状态</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="course in courses" :key="course.id">
            <td>
              <div class="title-cell">
                <span class="course-title clickable" @click="goToEnrollmentApproval(course.id)">{{ course.title }}</span>
                <span class="course-summary" v-if="course.summary">{{ course.summary }}</span>
              </div>
            </td>
            <td>{{ course.teacher || '—' }}</td>
            <td>
              <div class="tags-cell">
                <span v-for="tag in course.tags" :key="tag.id" class="tag-chip-small">
                  {{ tag.name }}
                </span>
                <span v-if="!course.tags || course.tags.length === 0">—</span>
              </div>
            </td>
            <td>
              <div class="time-cell">
                <span>{{ formatDate(course.startTime) }}</span>
                <span class="time-separator">至</span>
                <span>{{ formatDate(course.endTime) }}</span>
              </div>
            </td>
            <td>{{ course.enrolledCount }} / {{ course.quota }}（剩余 {{ course.remainingQuota }}）</td>
            <td>¥ {{ Number(course.price || 0).toLocaleString() }}</td>
            <td>
              <span :class="['status-tag', course.status?.toLowerCase(), { scheduled: !!course.scheduledPublishAt }]">
                {{ statusLabel(course.status) }}
                <template v-if="course.scheduledPublishAt">
                  <span class="schedule-hint">{{ formatScheduledHint(course.scheduledPublishAt) }}</span>
                </template>
              </span>
            </td>
            <td>
              <button v-if="course.status === 'ARCHIVED'" class="link-button" @click="openEditForm(course)">查看详情</button>
              <template v-else>
                <button class="link-button" @click="openEditForm(course)">编辑</button>
                <button class="danger-link" @click="removeCourse(course.id)" :disabled="loading">
                  删除
                </button>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="pagination">
        <button @click="changePage(pagination.page - 1)" :disabled="pagination.page === 0 || loading">上一页</button>
        <span>第 {{ pagination.page + 1 }} / {{ totalPages }} 页</span>
        <button @click="changePage(pagination.page + 1)" :disabled="pagination.page + 1 >= totalPages || loading">下一页</button>
      </div>
    </section>

    <div v-else class="empty-state">
      <p>暂无课程记录，请先创建。</p>
    </div>

    <div v-if="showForm" class="modal-backdrop">
      <div class="modal">
        <h3>{{ formMode === 'create' ? '新建课程' : (originalStatus === 'ARCHIVED' ? '课程详情' : '编辑课程') }}</h3>
        <form @submit.prevent="submitForm">
          <div class="form-grid">
            <label>
              <span>课程名称</span>
              <input v-model="formData.title" :disabled="originalStatus === 'ARCHIVED'" required />
            </label>
            <label>
              <span>讲师</span>
              <input v-model="formData.teacher" :disabled="originalStatus === 'ARCHIVED'" />
            </label>
            <label>
              <span>开课时间</span>
              <input type="date" v-model="formData.startTime" :disabled="originalStatus === 'ARCHIVED'" />
            </label>
            <label>
              <span>结课时间</span>
              <input type="date" v-model="formData.endTime" :disabled="originalStatus === 'ARCHIVED'" />
            </label>
            <label>
              <span>课程总名额</span>
              <input type="number" min="0" v-model.number="formData.quota" :disabled="originalStatus === 'ARCHIVED'" required />
            </label>
            <label>
              <span>推荐名额抵扣金额</span>
              <input type="number" min="0" step="0.01" v-model.number="formData.referralDeductionValue" :disabled="originalStatus === 'ARCHIVED' || isFreeCourse" />
            </label>
            <label>
              <span>生成推荐名额</span>
              <input type="number" min="0" step="0.01" v-model.number="formData.generatedReferralQuota" :disabled="originalStatus === 'ARCHIVED' || isFreeCourse" />
            </label>
            <label>
              <span>推荐名额有效周期（月）</span>
              <input type="number" min="0" v-model.number="formData.referralQuotaValidityPeriod" :disabled="originalStatus === 'ARCHIVED' || isFreeCourse" />
            </label>
            <p v-if="isFreeCourse && originalStatus !== 'ARCHIVED'" class="free-course-referral-hint">
              课程价格为 0 时不可配置推荐名额生成与抵扣；将价格改为大于 0 后可填写。
            </p>
            <label>
              <span>课程价格 (元)</span>
              <input type="number" min="0" step="0.01" v-model.number="formData.price" :disabled="originalStatus === 'ARCHIVED'" required />
            </label>
            <label class="full-width">
              <span>付款账号配置</span>
              <div v-if="isFreeCourse" class="payment-accounts-hint free-course-hint">
                课程价格为 0，无需配置付款账号
              </div>
              <div v-else class="payment-accounts-wrapper">
                <div v-for="(account, index) in formData.paymentAccounts" :key="index" class="payment-account-item">
                  <div class="account-fields">
                    <input type="text" v-model="account.bankName" :disabled="originalStatus === 'ARCHIVED'" placeholder="银行名称" class="account-input" />
                    <input type="text" v-model="account.bankAccount" :disabled="originalStatus === 'ARCHIVED'" placeholder="银行账号" class="account-input account-input-number" required />
                    <input type="text" v-model="account.accountHolder" :disabled="originalStatus === 'ARCHIVED'" placeholder="户名" class="account-input" />
                  </div>
                  <div class="account-actions" v-if="originalStatus !== 'ARCHIVED'">
                    <button type="button" class="icon-button move-up" @click="moveAccountUp(index)" :disabled="index === 0" title="上移">↑</button>
                    <button type="button" class="icon-button move-down" @click="moveAccountDown(index)" :disabled="index === formData.paymentAccounts.length - 1" title="下移">↓</button>
                    <button type="button" class="icon-button remove-account" @click="removePaymentAccount(index)" title="删除">×</button>
                  </div>
                </div>
                <div v-if="formData.paymentAccounts.length === 0" class="no-accounts-hint">
                  暂无付款账号，请添加至少一个账号
                </div>
                <button v-if="originalStatus !== 'ARCHIVED'" type="button" class="ghost-button add-account-btn" @click="addPaymentAccount">
                  + 添加付款账号
                </button>
                <div class="payment-accounts-hint">
                  配置多个账号时，审批通过的用户将按名额平均分配到不同账号
                </div>
              </div>
            </label>
            <label>
              <span>强制推荐人意见</span>
              <select v-model="formData.requireRecommenderOpinion" :disabled="originalStatus === 'ARCHIVED' || requireRecommenderOpinionLocked">
                <option :value="false">否</option>
                <option :value="true">是</option>
              </select>
              <small v-if="requireRecommenderOpinionLocked" class="recommender-opinion-lock-hint">该课程已有报名记录，不可修改是否强制推荐人意见</small>
            </label>
            <label>
              <span>需要解决的问题必填</span>
              <select v-model="formData.requireProblemDescription" :disabled="originalStatus === 'ARCHIVED' || requireProblemDescriptionLocked">
                <option :value="false">否</option>
                <option :value="true">是</option>
              </select>
              <small v-if="requireProblemDescriptionLocked" class="recommender-opinion-lock-hint">该课程已有报名记录，不可修改「需要解决的问题」是否必填</small>
            </label>
            <label>
              <span>课程状态</span>
              <select v-model="formData.status" :disabled="originalStatus === 'ARCHIVED' || formData.schedulePublish">
                <option v-for="option in availableStatusOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </label>
            <div
              v-if="schedulePublishEligible && originalStatus !== 'ARCHIVED'"
              class="full-width schedule-publish-field"
            >
              <span>{{ schedulePublishHeading }}</span>
              <div class="schedule-publish-controls">
                <div class="checkbox-inline">
                  <input
                    type="checkbox"
                    class="schedule-publish-checkbox"
                    v-model="formData.schedulePublish"
                    aria-describedby="schedule-publish-caption"
                  />
                  <span id="schedule-publish-caption" class="schedule-publish-caption">
                    {{ schedulePublishHelpText }}
                  </span>
                </div>
                <div v-if="formData.schedulePublish" class="schedule-datetime-split">
                  <div class="schedule-datetime-split-row">
                    <label class="schedule-subfield">
                      <span class="schedule-subfield-label">日期</span>
                      <input
                        ref="schedulePublishDateInput"
                        type="date"
                        v-model.lazy="formData.scheduledPublishDate"
                        autocomplete="off"
                        :disabled="originalStatus === 'ARCHIVED'"
                        @change="onScheduleDateOrTimePick"
                      />
                    </label>
                    <label class="schedule-subfield">
                      <span class="schedule-subfield-label">时间</span>
                      <input
                        ref="schedulePublishTimeInput"
                        type="time"
                        v-model.lazy="formData.scheduledPublishTime"
                        autocomplete="off"
                        :disabled="originalStatus === 'ARCHIVED'"
                        @change="onScheduleDateOrTimePick"
                      />
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <label class="full-width">
              <span>标签</span>
              <div v-if="availableTags.length > 0" class="tag-selector-wrapper" :class="{ disabled: originalStatus === 'ARCHIVED' }">
                <!-- 输入框区域，显示已选标签和下拉按钮 -->
                <div class="tag-input-container" @click="toggleTagDropdown" :class="{ disabled: originalStatus === 'ARCHIVED' }">
                  <div class="selected-tags-container">
                    <span 
                      v-for="selectedTagId in formData.tagIds" 
                      :key="selectedTagId" 
                      class="tag-chip"
                    >
                      {{ getTagNameById(selectedTagId) }}
                      <span 
                        v-if="originalStatus !== 'ARCHIVED'"
                        class="tag-remove" 
                        @click.stop="removeTag(selectedTagId)"
                      >×</span>
                    </span>
                    <span v-if="formData.tagIds.length === 0" class="tag-placeholder">请选择标签</span>
                  </div>
                  <span class="tag-dropdown-arrow" :class="{ open: showTagDropdown }">▼</span>
                </div>
                <!-- 下拉列表 -->
                <div v-if="showTagDropdown" class="tag-dropdown" @click.stop>
                  <div 
                    v-for="tag in availableTags" 
                    :key="tag.id" 
                    class="tag-dropdown-item"
                    :class="{ selected: isTagSelected(tag.id) }"
                    @click="toggleTagSelection(tag.id)"
                  >
                    <span class="tag-name">{{ tag.name }}</span>
                    <span v-if="isTagSelected(tag.id)" class="tag-check">✓</span>
                  </div>
                  <div v-if="availableTags.length === 0" class="tag-dropdown-empty">
                    暂无标签
                  </div>
                </div>
              </div>
              <div v-else class="no-tags-hint">
                暂无标签，请先在标签管理中创建标签
              </div>
            </label>
            <label class="full-width">
              <span>封面图片</span>
              <div class="image-upload-wrapper">
                <input 
                  type="file" 
                  ref="coverImageInput"
                  accept="image/*" 
                  @change="handleCoverImageChange"
                  style="display: none"
                />
                <div class="image-preview-container">
                  <img 
                    v-if="formData.coverImageUrl" 
                    :src="presignedUrls.cover || formData.coverImageUrl" 
                    alt="封面图片预览" 
                    class="image-preview"
                    @error="handleImageError('cover')"
                    @load="handleImageLoad('cover')"
                  />
                  <div v-else class="image-placeholder">暂无图片</div>
                  <div v-if="imageErrors.cover" class="image-error">图片加载失败</div>
                </div>
                <div class="image-upload-actions" v-if="originalStatus !== 'ARCHIVED'">
                  <button type="button" class="ghost-button" @click="$refs.coverImageInput.click()">
                    {{ formData.coverImageUrl ? '更换图片' : '选择图片' }}
                  </button>
                  <button v-if="formData.coverImageUrl" type="button" class="danger-link" @click="clearCoverImage">清除</button>
                  <span v-if="uploading.cover" class="upload-status">上传中...</span>
                </div>
              </div>
            </label>
            <label class="full-width">
              <span>轮播图</span>
              <div class="image-upload-wrapper">
                <input 
                  type="file" 
                  ref="bannerImageInput"
                  accept="image/*" 
                  @change="handleBannerImageChange"
                  style="display: none"
                />
                <div class="image-preview-container">
                  <img 
                    v-if="formData.bannerImageUrl" 
                    :src="presignedUrls.banner || formData.bannerImageUrl" 
                    alt="轮播图预览" 
                    class="image-preview"
                    @error="handleImageError('banner')"
                    @load="handleImageLoad('banner')"
                  />
                  <div v-else class="image-placeholder">暂无图片</div>
                  <div v-if="imageErrors.banner" class="image-error">图片加载失败</div>
                </div>
                <div class="image-upload-actions" v-if="originalStatus !== 'ARCHIVED'">
                  <button type="button" class="ghost-button" @click="$refs.bannerImageInput.click()">
                    {{ formData.bannerImageUrl ? '更换图片' : '选择图片' }}
                  </button>
                  <button v-if="formData.bannerImageUrl" type="button" class="danger-link" @click="clearBannerImage">清除</button>
                  <span v-if="uploading.banner" class="upload-status">上传中...</span>
                </div>
              </div>
            </label>
            <label class="full-width">
              <span>详情图</span>
              <div class="image-upload-wrapper">
                <input 
                  type="file" 
                  ref="detailImageInput"
                  accept="image/*" 
                  @change="handleDetailImageChange"
                  style="display: none"
                />
                <div class="image-preview-container">
                  <img 
                    v-if="formData.detailImageUrl" 
                    :src="presignedUrls.detail || formData.detailImageUrl" 
                    alt="详情图预览" 
                    class="image-preview"
                    @error="handleImageError('detail')"
                    @load="handleImageLoad('detail')"
                  />
                  <div v-else class="image-placeholder">暂无图片</div>
                  <div v-if="imageErrors.detail" class="image-error">图片加载失败</div>
                </div>
                <div class="image-upload-actions" v-if="originalStatus !== 'ARCHIVED'">
                  <button type="button" class="ghost-button" @click="$refs.detailImageInput.click()">
                    {{ formData.detailImageUrl ? '更换图片' : '选择图片' }}
                  </button>
                  <button v-if="formData.detailImageUrl" type="button" class="danger-link" @click="clearDetailImage">清除</button>
                  <span v-if="uploading.detail" class="upload-status">上传中...</span>
                </div>
              </div>
            </label>
            <label class="full-width">
              <span>课程摘要</span>
              <textarea rows="3" v-model="formData.summary" :disabled="originalStatus === 'ARCHIVED'"></textarea>
            </label>
            <label class="full-width">
              <span>课程介绍</span>
              <textarea rows="5" v-model="formData.description" :disabled="originalStatus === 'ARCHIVED'"></textarea>
            </label>
            <div class="full-width">
              <fieldset class="section-fieldset">
                <legend>内容板块</legend>
                <div class="cs-toolbar">
                  <span class="cs-hint">在小程序详情页按顺序展示（与康养项目一致）；留空时使用下方「课程介绍」富文本。</span>
                  <div v-if="originalStatus !== 'ARCHIVED'" class="cs-add-btns">
                    <button type="button" class="ghost-button cs-add-btn" @click="addSection('text')">+ 纯文本</button>
                    <button type="button" class="ghost-button cs-add-btn" @click="addSection('numberedList')">+ 编号列表</button>
                    <button type="button" class="ghost-button cs-add-btn" @click="addSection('bulletList')">+ 圆点列表</button>
                    <button type="button" class="ghost-button cs-add-btn" @click="addSection('highlight')">+ 重点提示</button>
                    <button type="button" class="ghost-button cs-add-btn" @click="addSection('channelVideo')">+ 视频号视频</button>
                    <button type="button" class="ghost-button cs-add-btn" @click="addSection('externalVideo')">+ 外部视频</button>
                  </div>
                </div>
                <div v-if="!formData.contentSections || !formData.contentSections.length" class="cs-empty">暂无内容板块，点击上方按钮添加。</div>
                <div v-for="(section, sIdx) in formData.contentSections" :key="sIdx" class="cs-card">
                  <div class="cs-card-header">
                    <input v-model="section.title" class="cs-title-input" placeholder="板块标题" :disabled="originalStatus === 'ARCHIVED'" />
                    <span class="cs-type-badge">{{ sectionTypeLabels[section.type] || section.type }}</span>
                    <div v-if="originalStatus !== 'ARCHIVED'" class="cs-card-actions">
                      <button v-if="sIdx > 0" type="button" class="cs-action-btn" @click="moveSection(sIdx, -1)" title="上移">&#8593;</button>
                      <button v-if="sIdx < formData.contentSections.length - 1" type="button" class="cs-action-btn" @click="moveSection(sIdx, 1)" title="下移">&#8595;</button>
                      <button type="button" class="cs-action-btn cs-remove-btn" @click="removeSection(sIdx)" title="删除">&#10005;</button>
                    </div>
                  </div>
                  <div v-if="section.type === 'text'" class="cs-card-body">
                    <textarea v-model="section.content" class="cs-textarea" placeholder="输入纯文本内容…" rows="4" :disabled="originalStatus === 'ARCHIVED'"></textarea>
                  </div>
                  <div v-if="section.type === 'numberedList'" class="cs-card-body">
                    <div v-for="(item, iIdx) in section.items" :key="iIdx" class="cs-item-row">
                      <span class="cs-item-num">{{ iIdx + 1 }}</span>
                      <input v-model="item.title" class="cs-item-title" placeholder="条目标题（可选）" :disabled="originalStatus === 'ARCHIVED'" />
                      <input v-model="item.description" class="cs-item-desc" placeholder="条目描述" :disabled="originalStatus === 'ARCHIVED'" />
                      <button v-if="originalStatus !== 'ARCHIVED'" type="button" class="cs-action-btn cs-remove-btn" @click="section.items.splice(iIdx, 1)">&#10005;</button>
                    </div>
                    <button v-if="originalStatus !== 'ARCHIVED'" type="button" class="ghost-button" @click="section.items.push({ title: '', description: '' })">+ 添加条目</button>
                  </div>
                  <div v-if="section.type === 'bulletList'" class="cs-card-body">
                    <div v-for="(item, iIdx) in section.items" :key="iIdx" class="cs-item-row">
                      <span class="cs-item-bullet">&#8226;</span>
                      <input v-model="item.title" class="cs-item-desc" placeholder="关键词或短语" :disabled="originalStatus === 'ARCHIVED'" />
                      <button v-if="originalStatus !== 'ARCHIVED'" type="button" class="cs-action-btn cs-remove-btn" @click="section.items.splice(iIdx, 1)">&#10005;</button>
                    </div>
                    <button v-if="originalStatus !== 'ARCHIVED'" type="button" class="ghost-button" @click="section.items.push({ title: '', description: '' })">+ 添加条目</button>
                  </div>
                  <div v-if="section.type === 'highlight'" class="cs-card-body cs-highlight-body">
                    <div v-for="(item, iIdx) in section.items" :key="iIdx" class="cs-item-row">
                      <span class="cs-item-num">{{ iIdx + 1 }}</span>
                      <input v-model="item.title" class="cs-item-title" placeholder="条目标题（可选）" :disabled="originalStatus === 'ARCHIVED'" />
                      <input v-model="item.description" class="cs-item-desc" placeholder="条目描述" :disabled="originalStatus === 'ARCHIVED'" />
                      <button v-if="originalStatus !== 'ARCHIVED'" type="button" class="cs-action-btn cs-remove-btn" @click="section.items.splice(iIdx, 1)">&#10005;</button>
                    </div>
                    <button v-if="originalStatus !== 'ARCHIVED'" type="button" class="ghost-button" @click="section.items.push({ title: '', description: '' })">+ 添加条目</button>
                  </div>
                  <div v-if="section.type === 'channelVideo'" class="cs-card-body cs-video-fields">
                    <p class="cs-inline-hint">
                      报错「<code>1416103</code>」常见原因：
                      （1）实际为非同主体却仍传 feed-id；
                      （2）小程序 / 视频号主体名称标点不一致（如中英文括号）
                      （3）视频号未认证或认证过期。<strong>若为非同主体</strong>（基础库≥2.31.1），勾选下方选项并仅用官方指引获取的
                      <strong>feed-token</strong>。<a href="https://developers.weixin.qq.com/miniprogram/dev/framework/open-ability/channels-activity.html#feed-token" target="_blank" rel="noopener noreferrer">feed-token 文档</a> ·
                      <a href="https://channels.weixin.qq.com/" target="_blank" rel="noopener noreferrer">视频号助手</a>
                    </p>
                    <label class="cs-field-block cs-checkbox-row">
                      <input
                        type="checkbox"
                        v-model="section.channelVideoCrossSubject"
                        :disabled="originalStatus === 'ARCHIVED'"
                      />
                      <span>非同主体嵌入（只用 finderUserName + feed-token，不向小程序传 feed-id）</span>
                    </label>
                    <label class="cs-field-block">
                      <span>视频号 ID（finderUserName）</span>
                      <input v-model="section.finderUserName" class="cs-title-input" placeholder="如 sphxxxx" :disabled="originalStatus === 'ARCHIVED'" />
                    </label>
                    <label v-if="!section.channelVideoCrossSubject" class="cs-field-block">
                      <span>feed-id（同主体）</span>
                      <input v-model="section.feedId" class="cs-title-input" placeholder="从助手一键完整复制 export/... 切勿截断" :disabled="originalStatus === 'ARCHIVED'" />
                    </label>
                    <label class="cs-field-block">
                      <span>{{ section.channelVideoCrossSubject ? 'feed-token（非同主体必填）' : 'feed-token（非同主体时请改用上方勾选；此处可与 feed-id 同主体时通常留空）' }}</span>
                      <input
                        v-model="section.feedToken"
                        class="cs-title-input"
                        :placeholder="section.channelVideoCrossSubject ? '按官方文档获取 token/...' : '同主体一般不填'"
                        :disabled="originalStatus === 'ARCHIVED'"
                      />
                    </label>
                  </div>
                  <div v-if="section.type === 'externalVideo'" class="cs-card-body cs-video-fields">
                    <p class="cs-inline-hint">
                      视频地址须为 <strong>https://</strong>；小程序须在公众平台配置该域名为「downloadFile 合法域名」后方可播放。
                    </p>
                    <label class="cs-field-block">
                      <span>视频 URL</span>
                      <input v-model="section.videoUrl" class="cs-title-input" placeholder="https://...（MP4 / M3U8 等）" :disabled="originalStatus === 'ARCHIVED'" />
                    </label>
                    <label class="cs-field-block">
                      <span>封面图 URL（可选）</span>
                      <div class="cs-poster-upload-row">
                        <input v-model="section.posterUrl" class="cs-title-input cs-poster-input" placeholder="https://... 或点击下方上传" :disabled="originalStatus === 'ARCHIVED'" />
                        <button
                          v-if="originalStatus !== 'ARCHIVED'"
                          type="button"
                          class="ghost-button cs-poster-upload-btn"
                          :disabled="uploadingSectionPoster"
                          @click="triggerSectionPosterPick(sIdx)"
                        >
                          {{ uploadingSectionPoster ? '上传中…' : '上传封面' }}
                        </button>
                      </div>
                    </label>
                  </div>
                </div>
                <input
                  ref="sectionPosterFileInput"
                  type="file"
                  accept="image/*"
                  class="cs-hidden-file-input"
                  aria-hidden="true"
                  @change="handleSectionPosterFileChange"
                />
              </fieldset>
            </div>
          </div>
          <div class="modal-actions">
            <button type="button" class="ghost-button" @click="closeForm">{{ originalStatus === 'ARCHIVED' ? '关闭' : '取消' }}</button>
            <button v-if="originalStatus !== 'ARCHIVED'" type="submit" class="primary-button" :disabled="loading">
              {{ loading ? '提交中...' : '保存' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { courseService } from '@/services/courseService'
import { tagService } from '@/services/tagService'

const STATUS_MAP = {
  DRAFT: '草稿',
  PUBLISHED: '待报名',
  ENROLLING: '报名中',
  ONGOING: '进行中',
  COMPLETED: '已结课',
  ARCHIVED: '已归档'
}

export default {
  name: 'CourseManagement',
  data() {
    return {
      courses: [],
      loading: false,
      showForm: false,
      formMode: 'create',
      formData: this.getEmptyForm(),
      filters: {
        keyword: '',
        status: '',
        startDate: '',
        endDate: ''
      },
      pagination: {
        page: 0,
        size: 10,
        total: 0
      },
      statusOptions: Object.entries(STATUS_MAP).map(([value, label]) => ({ value, label })),
      availableTags: [],
      uploading: {
        cover: false,
        banner: false,
        detail: false
      },
      imageErrors: {
        cover: false,
        banner: false,
        detail: false
      },
      presignedUrls: {
        cover: null,
        banner: null,
        detail: null
      },
      originalStatus: null, // 保存编辑时的原始状态
      /** 打开编辑弹窗时快照的已报名人数（用于隐藏不可选状态） */
      editingCourseEnrolledTotal: 0,
      showTagDropdown: false, // 控制标签下拉列表显示
      sectionTypeLabels: {
        text: '纯文本',
        numberedList: '编号列表',
        bulletList: '圆点列表',
        highlight: '重点提示',
        channelVideo: '视频号视频',
        externalVideo: '外部视频'
      },
      pendingPosterSectionIdx: null,
      uploadingSectionPoster: false
    }
  },
  computed: {
    /** 新建或仍为草稿的课程可设置定时上线 */
    schedulePublishEligible() {
      return (
        this.formMode === 'create' ||
        !this.originalStatus ||
        this.originalStatus === 'DRAFT' ||
        this.originalStatus === 'PUBLISHED'
      )
    },
    /** 勾选定时时的基线状态：服务端仅允许「草稿定时上线」或「待报名定时开启报名」。
     * 编辑草稿时用户在表单中选「待报名」后勾选定时，必须按表单意向走待报名（不能仅靠 originalStatus，否则会被误当成草稿）。
     */
    schedulePublishUsesPublishedBaseline() {
      return this.originalStatus === 'PUBLISHED' || this.formData.status === 'PUBLISHED'
    },
    schedulePublishHeading() {
      return this.schedulePublishUsesPublishedBaseline ? '定时开启报名' : '定时上线'
    },
    schedulePublishHelpText() {
      return this.schedulePublishUsesPublishedBaseline
        ? '预设开启报名时间（仍为待报名；请分别选择日期与时间，保存后已到点自动变为报名中）'
        : '预设上线时间（仍为草稿；请分别选择日期与时间，保存后已到点自动变为报名中）'
    },
    isFreeCourse() {
      return Number(this.formData.price || 0) === 0
    },
    /** 报名中且已有报名时，禁止改动「强制推荐人意见」（与后端一致） */
    requireRecommenderOpinionLocked() {
      return this.originalStatus === 'ENROLLING' && this.editingCourseEnrolledTotal > 0
    },
    /** 报名中且已有报名时，禁止改动「需要解决的问题是否必填」（与后端一致） */
    requireProblemDescriptionLocked() {
      return this.originalStatus === 'ENROLLING' && this.editingCourseEnrolledTotal > 0
    },
    totalPages() {
      return Math.ceil(this.pagination.total / this.pagination.size) || 1
    },
    availableStatusOptions() {
      // 使用原始状态来判断，而不是当前表单中的状态
      // 如果原始状态是已归档，返回空数组（不可修改）
      if (this.originalStatus === 'ARCHIVED') {
        return [{ value: 'ARCHIVED', label: STATUS_MAP['ARCHIVED'] }]
      }
      // 如果原始状态是已结课，可以选择已结课或已归档
      if (this.originalStatus === 'COMPLETED') {
        return this.statusOptions.filter(option => option.value === 'COMPLETED' || option.value === 'ARCHIVED')
      }
      // 其他状态（草稿、报名中、进行中）不能直接改为已归档，也不能改为已结课（只能通过后端逻辑自动结课，或者允许手动改？后端逻辑是从COMPLETED->ARCHIVED限制，前端这里只限制不能看到ARCHIVED）
      // 根据用户需求：只能从已结课状态才能改到已归档。所以非已结课状态不显示已归档。
      let options = this.statusOptions.filter(option => option.value !== 'ARCHIVED')
      if (this.originalStatus === 'ENROLLING' && this.editingCourseEnrolledTotal > 0) {
        options = options.filter(option => option.value !== 'DRAFT')
      }
      return options
    }
  },
  watch: {
    'formData.coverImageUrl'(newUrl) {
      if (newUrl && this.isOssPrivateUrl(newUrl) && !this.presignedUrls.cover) {
        this.loadPresignedUrl('cover', newUrl)
      }
    },
    'formData.bannerImageUrl'(newUrl) {
      if (newUrl && this.isOssPrivateUrl(newUrl) && !this.presignedUrls.banner) {
        this.loadPresignedUrl('banner', newUrl)
      }
    },
    'formData.detailImageUrl'(newUrl) {
      if (newUrl && this.isOssPrivateUrl(newUrl) && !this.presignedUrls.detail) {
        this.loadPresignedUrl('detail', newUrl)
      }
    },
    'formData.schedulePublish'(v) {
      if (v && this.schedulePublishEligible) {
        this.formData.status = this.schedulePublishUsesPublishedBaseline ? 'PUBLISHED' : 'DRAFT'
      }
      if (!v) {
        this.formData.scheduledPublishDate = ''
        this.formData.scheduledPublishTime = ''
      }
    },
    'formData.price'(val) {
      if (Number(val || 0) === 0) {
        this.formData.referralDeductionValue = 0
        this.formData.generatedReferralQuota = 0
        this.formData.referralQuotaValidityPeriod = 0
      }
    }
  },
  created() {
    this.loadCourses()
    this.loadTags()
    // 点击外部区域关闭标签下拉列表
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    getEmptyForm() {
      return {
        id: null,
        title: '',
        summary: '',
        description: '',
        teacher: '',
        coverImageUrl: '',
        bannerImageUrl: '',
        detailImageUrl: '',
        startTime: '',
        endTime: '',
        quota: 0,
        referralDeductionValue: 0,
        generatedReferralQuota: 0,
        referralQuotaValidityPeriod: 0,
        price: 0,
        bankName: '',
        bankAccount: '',
        accountHolder: '',
        requireRecommenderOpinion: false,
        requireProblemDescription: true,
        status: 'DRAFT',
        schedulePublish: false,
        scheduledPublishDate: '',
        scheduledPublishTime: '',
        tagIds: [],
        paymentAccounts: [],
        contentSections: []
      }
    },
    addSection(type) {
      if (!this.formData.contentSections) {
        this.formData.contentSections = []
      }
      const section = { title: '', type }
      if (type === 'text') {
        section.content = ''
      } else if (type === 'numberedList') {
        section.items = [{ title: '', description: '' }]
      } else if (type === 'bulletList') {
        section.items = [{ title: '', description: '' }]
      } else if (type === 'highlight') {
        section.items = [{ title: '', description: '' }]
      } else if (type === 'channelVideo') {
        section.finderUserName = ''
        section.feedId = ''
        section.feedToken = ''
        section.channelVideoCrossSubject = false
      } else if (type === 'externalVideo') {
        section.videoUrl = ''
        section.posterUrl = ''
      }
      this.formData.contentSections.push(section)
    },
    removeSection(idx) {
      this.formData.contentSections.splice(idx, 1)
    },
    moveSection(idx, delta) {
      const arr = this.formData.contentSections
      const newIdx = idx + delta
      if (newIdx < 0 || newIdx >= arr.length) return
      const temp = arr[idx]
      arr.splice(idx, 1)
      arr.splice(newIdx, 0, temp)
      this.formData.contentSections = [...arr]
    },
    validateCourseContentSectionsBeforeSave() {
      const sections = this.formData.contentSections || []
      for (let i = 0; i < sections.length; i++) {
        const s = sections[i]
        if (!s || !s.type) continue
        const n = i + 1
        if (s.type === 'channelVideo') {
          const cross = !!s.channelVideoCrossSubject
          const fun = String(s.finderUserName || '').trim()
          if (!fun) {
            alert(`内容板块第 ${n} 个（视频号视频）：请填写视频号 ID（finderUserName）`)
            return false
          }
          if (cross) {
            const tok = String(s.feedToken || '').trim()
            if (!tok) {
              alert(`内容板块第 ${n} 个（视频号视频·非同主体）：请填写 feed-token`)
              return false
            }
          } else {
            const fid = String(s.feedId || '').trim()
            if (!fid) {
              alert(
                `内容板块第 ${n} 个（视频号视频）：请填写 feed-id（同主体）；若为非同主体请勾选「非同主体嵌入」并填 feed-token`
              )
              return false
            }
          }
        }
        if (s.type === 'externalVideo') {
          const u = String(s.videoUrl || '').trim()
          if (!u) {
            alert(`内容板块第 ${n} 个（外部视频）：请填写视频 HTTPS 地址`)
            return false
          }
          if (!u.startsWith('https://')) {
            alert(`内容板块第 ${n} 个（外部视频）：视频地址必须以 https:// 开头`)
            return false
          }
          const poster = String(s.posterUrl || '').trim()
          if (poster && !poster.startsWith('https://')) {
            alert(`内容板块第 ${n} 个（外部视频）：封面图地址必须以 https:// 开头`)
            return false
          }
        }
      }
      return true
    },
    triggerSectionPosterPick(sectionIdx) {
      if (this.originalStatus === 'ARCHIVED') return
      this.pendingPosterSectionIdx = sectionIdx
      this.$nextTick(() => {
        const el = this.$refs.sectionPosterFileInput
        if (el) el.click()
      })
    },
    async handleSectionPosterFileChange(event) {
      const idx = this.pendingPosterSectionIdx
      this.pendingPosterSectionIdx = null
      const file = event.target.files && event.target.files[0]
      if (event.target) {
        event.target.value = ''
      }
      if (!file || idx === null || idx === undefined) return
      if (!file.type.startsWith('image/')) {
        alert('请选择图片文件')
        return
      }
      if (file.size > 10 * 1024 * 1024) {
        alert('图片大小不能超过10MB')
        return
      }
      const section = this.formData.contentSections && this.formData.contentSections[idx]
      if (!section || section.type !== 'externalVideo') return
      try {
        this.uploadingSectionPoster = true
        const response = await courseService.uploadImage(file)
        if (response.success && response.data && response.data.url) {
          section.posterUrl = response.data.url
        } else {
          alert('封面上传失败')
        }
      } catch (error) {
        console.error('上传视频封面失败', error)
        alert('封面上传失败：' + (error.response?.data?.message || error.message))
      } finally {
        this.uploadingSectionPoster = false
      }
    },
    formatDate(value) {
      if (!value) return '—'
      // 只显示日期部分（YYYY-MM-DD），不显示时间
      if (value.includes(' ')) {
        return value.split(' ')[0]
      }
      if (value.includes('T')) {
        return value.split('T')[0]
      }
      return value.substring(0, 10)
    },
    statusLabel(status) {
      return STATUS_MAP[status] || status || '—'
    },
    formatScheduledHint(isoOrServer) {
      if (!isoOrServer) return ''
      const s = String(isoOrServer).trim().replace('T', ' ')
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[\s](\d{2}:\d{2})/)
      if (!m) return ''
      return `定时 ${m[2]}-${m[3]} ${m[4]}`
    },
    /**
     * 将后端/接口传来的时间规整为 yyyy-MM-ddTHH:mm（Safari 对 datetime-local 支持差，故拆成 date+time 控件用此解析）
     */
    normalizeScheduledCombinedFromApi(raw) {
      if (raw === null || raw === undefined || String(raw).trim() === '') return ''
      let s = String(raw).trim()
      try {
        if (/[zZ]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s)) {
          const d = new Date(s.includes('T') ? s.replace(' ', 'T') : s)
          if (Number.isNaN(d.getTime())) return ''
          const y = d.getFullYear()
          const m = String(d.getMonth() + 1).padStart(2, '0')
          const day = String(d.getDate()).padStart(2, '0')
          const h = String(d.getHours()).padStart(2, '0')
          const mi = String(d.getMinutes()).padStart(2, '0')
          return `${y}-${m}-${day}T${h}:${mi}`
        }
        if (!s.includes('T')) {
          const mSep = s.match(/^(\d{4}-\d{2}-\d{2})[\sT](.+)$/)
          if (mSep) s = `${mSep[1]}T${mSep[2]}`
          else return ''
        }
        const parts = s.split('T')
        if (parts.length < 2) return ''
        const datePart = parts[0].trim()
        const timeRest = parts.slice(1).join('T')
        if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return ''
        const mTime = String(timeRest).match(/^(\d{1,2}):(\d{1,2})/)
        if (!mTime) return ''
        const hn = parseInt(mTime[1], 10)
        const mn = parseInt(mTime[2], 10)
        if (!Number.isFinite(hn) || !Number.isFinite(mn)) return ''
        if (hn < 0 || hn > 23 || mn < 0 || mn > 59) return ''
        const hh = String(hn).padStart(2, '0')
        const mm = String(mn).padStart(2, '0')
        return `${datePart}T${hh}:${mm}`
      } catch (_) {
        return ''
      }
    },
    /** 回填「日期」「时间」两个控件 */
    scheduledPublishPartsFromApi(raw) {
      const combined = this.normalizeScheduledCombinedFromApi(raw)
      if (!combined) return { date: '', time: '' }
      const tIdx = combined.indexOf('T')
      return {
        date: combined.slice(0, tIdx),
        time: combined.slice(tIdx + 1, tIdx + 6)
      }
    },
    /** 从前端 date + time 拼出 yyyy-MM-ddTHH:mm */
    combineScheduledPublishParts(dateStr, timeStr) {
      const d = String(dateStr || '').trim()
      if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return ''
      const tm = String(timeStr || '').trim().match(/^(\d{1,2}):(\d{1,2})(?::\d{1,2}(?:\.\d+)?)?/)
      if (!tm) return ''
      const hn = parseInt(tm[1], 10)
      const mn = parseInt(tm[2], 10)
      if (!Number.isFinite(hn) || !Number.isFinite(mn)) return ''
      if (hn < 0 || hn > 23 || mn < 0 || mn > 59) return ''
      const hh = String(hn).padStart(2, '0')
      const mi = String(mn).padStart(2, '0')
      return `${d}T${hh}:${mi}`
    },
    /** Safari 等对 date/time 有时不在 input 时机更新 v-model，提交前再从 DOM 读一遍 */
    syncSchedulePublishFromDom() {
      const dEl = this.$refs.schedulePublishDateInput
      const tEl = this.$refs.schedulePublishTimeInput
      const d =
        dEl && typeof dEl === 'object' && 'value' in dEl && typeof dEl.value === 'string' ? dEl.value : ''
      const t =
        tEl && typeof tEl === 'object' && 'value' in tEl && typeof tEl.value === 'string' ? tEl.value : ''
      if (d) this.formData.scheduledPublishDate = d
      if (t) this.formData.scheduledPublishTime = t
    },
    onScheduleDateOrTimePick() {
      this.syncSchedulePublishFromDom()
    },
    toServerScheduledAt(dtLocal) {
      if (!dtLocal) return null
      const parts = dtLocal.includes('T') ? dtLocal.split('T') : dtLocal.split(' ')
      if (parts.length !== 2) return null
      const tm = parts[1].length === 5 ? `${parts[1]}:00` : parts[1]
      return `${parts[0]} ${tm}`
    },
    async loadCourses() {
      try {
        this.loading = true
        const params = {
          page: this.pagination.page,
          size: this.pagination.size,
          keyword: this.filters.keyword || undefined,
          status: this.filters.status || undefined,
          startFrom: this.filters.startDate ? `${this.filters.startDate} 00:00:00` : undefined,
          endTo: this.filters.endDate ? `${this.filters.endDate} 23:59:59` : undefined
        }
        const response = await courseService.fetchCourses(params)
        if (response.success) {
          this.courses = response.data.content || []
          this.pagination.total = response.data.totalElements || 0
        }
      } catch (error) {
        console.error('加载课程失败', error)
      } finally {
        this.loading = false
      }
    },
    resetFilters() {
      this.filters = {
        keyword: '',
        status: '',
        startDate: '',
        endDate: ''
      }
      this.pagination.page = 0
      this.loadCourses()
    },
    changePage(page) {
      if (page < 0) return
      if (page >= this.totalPages) return
      this.pagination.page = page
      this.loadCourses()
    },
    openCreateForm() {
      this.formMode = 'create'
      this.formData = this.getEmptyForm()
      this.originalStatus = null // 新建时没有原始状态
      this.editingCourseEnrolledTotal = 0
      this.showForm = true
    },
    openEditForm(course) {
      this.formMode = 'edit'
      // 清除之前的预签名URL缓存
      this.presignedUrls = {
        cover: null,
        banner: null,
        detail: null
      }
      // 保存原始状态
      this.originalStatus = course.status || 'DRAFT'
      this.editingCourseEnrolledTotal = Number(course.enrolledCount || 0)
      // 提取标签ID列表
      const tagIds = course.tags && course.tags.length > 0 
        ? course.tags.map(tag => tag.id)
        : []
      // 提取付款账号列表
      const paymentAccounts = course.paymentAccounts && course.paymentAccounts.length > 0
        ? course.paymentAccounts.map(account => ({
            id: account.id,
            bankName: account.bankName || '',
            bankAccount: account.bankAccount || '',
            accountHolder: account.accountHolder || '',
            displayOrder: account.displayOrder || 0
          }))
        : []
      const scheduleParts = course.scheduledPublishAt
        ? this.scheduledPublishPartsFromApi(course.scheduledPublishAt)
        : { date: '', time: '' }
      this.formData = {
        id: course.id,
        title: course.title,
        summary: course.summary,
        description: course.description,
        teacher: course.teacher,
        coverImageUrl: course.coverImageUrl,
        bannerImageUrl: course.bannerImageUrl,
        detailImageUrl: course.detailImageUrl,
        startTime: this.toDateLocal(course.startTime),
        endTime: this.toDateLocal(course.endTime),
        quota: course.quota,
        referralDeductionValue: Number(course.referralDeductionValue || 0),
        generatedReferralQuota: Number(course.generatedReferralQuota || 0),
        referralQuotaValidityPeriod: Number(course.referralQuotaValidityPeriod || 0),
        price: Number(course.price || 0),
        bankName: course.bankName || '',
        bankAccount: course.bankAccount || '',
        accountHolder: course.accountHolder || '',
        requireRecommenderOpinion: course.requireRecommenderOpinion || false,
        requireProblemDescription: course.requireProblemDescription !== false,
        status: course.status || 'DRAFT',
        schedulePublish: !!(course.scheduledPublishAt && String(course.scheduledPublishAt).trim()),
        scheduledPublishDate: scheduleParts.date,
        scheduledPublishTime: scheduleParts.time,
        tagIds: tagIds,
        paymentAccounts: paymentAccounts,
        contentSections: (course.contentSections || []).map(s => {
          const copy = { ...s }
          if (copy.items) copy.items = copy.items.map(it => ({ ...it }))
          if (copy.type === 'channelVideo') {
            copy.channelVideoCrossSubject = !!(copy.channelVideoCrossSubject === true || copy.channelVideoCrossSubject === 'true')
          }
          return copy
        })
      }
      this.showForm = true
      // watch会自动触发加载预签名URL
    },
    closeForm() {
      this.showForm = false
      this.formData = this.getEmptyForm()
      this.originalStatus = null
      this.editingCourseEnrolledTotal = 0
      this.pendingPosterSectionIdx = null
      this.uploadingSectionPoster = false
    },
    toDateLocal(value) {
      if (!value) return ''
      // 从日期时间字符串中提取日期部分（YYYY-MM-DD）
      if (value.includes(' ')) {
        return value.split(' ')[0]
      }
      if (value.includes('T')) {
        return value.split('T')[0]
      }
      return value.substring(0, 10)
    },
    toServerDateTime(value, isEndTime = false) {
      if (!value) return null
      // 如果只有日期（YYYY-MM-DD格式），添加时间部分
      if (value.length === 10 && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // 开课时间设为00:00:00，结课时间设为23:59:59
        return isEndTime ? `${value} 23:59:59` : `${value} 00:00:00`
      }
      // 如果已经是完整的日期时间格式，提取日期部分并添加时间
      if (value.includes('T')) {
        const datePart = value.split('T')[0]
        return isEndTime ? `${datePart} 23:59:59` : `${datePart} 00:00:00`
      }
      if (value.includes(' ')) {
        const datePart = value.split(' ')[0]
        return isEndTime ? `${datePart} 23:59:59` : `${datePart} 00:00:00`
      }
      return value
    },
    async submitForm() {
      if (!this.formData.title) return
      // 已归档的课程不允许提交
      if (this.originalStatus === 'ARCHIVED') {
        return
      }
      if (!this.isFreeCourse) {
        const hasValidAccount = (this.formData.paymentAccounts || []).some(
          account => account.bankAccount && account.bankAccount.trim()
        )
        if (!hasValidAccount) {
          alert('付费课程请至少配置一个付款账号')
          return
        }
      }
      if (this.isFreeCourse) {
        const rd = Number(this.formData.referralDeductionValue || 0)
        const gen = Number(this.formData.generatedReferralQuota || 0)
        const vp = Number(this.formData.referralQuotaValidityPeriod || 0)
        if (gen > 0 || vp > 0) {
          alert('免费课程（价格为0）不能配置推荐名额生成规则')
          return
        }
        if (rd > 0) {
          alert('免费课程（价格为0）不能配置推荐名额抵扣金额')
          return
        }
      }
      if (this.schedulePublishEligible && this.formData.schedulePublish) {
        await this.$nextTick()
        this.syncSchedulePublishFromDom()
        const d = String(this.formData.scheduledPublishDate || '').trim()
        const t = String(this.formData.scheduledPublishTime || '').trim()
        if (!d || !t) {
          if (!d && !t) alert('请选择定时上线的日期和时间')
          else if (!d) alert('请选择定时上线的日期')
          else alert('请选择定时上线的时间')
          return
        }
        const combined = this.combineScheduledPublishParts(d, t)
        if (!combined) {
          alert('定时上线时间不完整或无效，请检查日期与时间')
          return
        }
      }
      if (!this.validateCourseContentSectionsBeforeSave()) {
        return
      }
      try {
        this.loading = true
        // 处理付款账号列表，设置displayOrder
        const paymentAccountsPayload = this.isFreeCourse
          ? []
          : (this.formData.paymentAccounts || [])
          .filter(account => account.bankAccount && account.bankAccount.trim())
          .map((account, index) => ({
            id: account.id,
            bankName: account.bankName,
            bankAccount: account.bankAccount,
            accountHolder: account.accountHolder,
            displayOrder: index
          }))

        let payloadStatus = this.formData.status
        let scheduledPublishAt = null
        if (this.schedulePublishEligible && this.formData.schedulePublish) {
          await this.$nextTick()
          this.syncSchedulePublishFromDom()
          payloadStatus = this.schedulePublishUsesPublishedBaseline ? 'PUBLISHED' : 'DRAFT'
          const combined = this.combineScheduledPublishParts(
            this.formData.scheduledPublishDate,
            this.formData.scheduledPublishTime
          )
          scheduledPublishAt = this.toServerScheduledAt(combined)
        } else if (this.schedulePublishEligible) {
          scheduledPublishAt = null
        }

        const payload = {
          title: this.formData.title,
          summary: this.formData.summary,
          description: this.formData.description,
          teacher: this.formData.teacher,
          coverImageUrl: this.formData.coverImageUrl,
          bannerImageUrl: this.formData.bannerImageUrl,
          detailImageUrl: this.formData.detailImageUrl,
          startTime: this.toServerDateTime(this.formData.startTime, false),
          endTime: this.toServerDateTime(this.formData.endTime, true),
          quota: this.formData.quota,
          referralDeductionValue: Number(this.formData.referralDeductionValue || 0),
          generatedReferralQuota: Number(this.formData.generatedReferralQuota || 0),
          referralQuotaValidityPeriod: Number(this.formData.referralQuotaValidityPeriod || 0),
          price: this.formData.price,
          bankName: this.formData.bankName,
          bankAccount: this.formData.bankAccount,
          accountHolder: this.formData.accountHolder,
          requireRecommenderOpinion: this.formData.requireRecommenderOpinion || false,
          requireProblemDescription: this.formData.requireProblemDescription !== false,
          status: payloadStatus,
          scheduledPublishAt,
          tagIds: this.formData.tagIds || [],
          paymentAccounts: paymentAccountsPayload,
          contentSections: this.formData.contentSections || []
        }

        if (this.formMode === 'create') {
          await courseService.createCourse(payload)
        } else if (this.formData.id) {
          await courseService.updateCourse(this.formData.id, payload)
        }
        this.closeForm()
        this.loadCourses()
      } catch (error) {
        console.error('保存课程失败', error)
        // 显示错误信息
        const responseData = error.response?.data
        const errorMessage = responseData?.error || responseData?.message || error.message || '保存课程失败'
        if (errorMessage.includes('课程名称已存在') || errorMessage.includes('不能重复')) {
          alert('课程名称已存在，不能重复，请使用其他名称')
        } else {
          alert(errorMessage)
        }
      } finally {
        this.loading = false
      }
    },
    async removeCourse(id) {
      if (!confirm('确定要删除该课程吗？删除后不可恢复。')) {
        return
      }
      try {
        this.loading = true
        await courseService.deleteCourse(id)
        this.loadCourses()
      } catch (error) {
        console.error('删除课程失败', error)
        const responseData = error.response?.data
        const errorMessage = responseData?.error || responseData?.message || error.message || '删除课程失败'
        alert(errorMessage)
      } finally {
        this.loading = false
      }
    },
    async handleCoverImageChange(event) {
      const file = event.target.files[0]
      if (!file) return
      
      // 验证文件类型
      if (!file.type.startsWith('image/')) {
        alert('请选择图片文件')
        return
      }
      
      // 验证文件大小（10MB）
      if (file.size > 10 * 1024 * 1024) {
        alert('图片大小不能超过10MB')
        return
      }
      
      try {
        this.uploading.cover = true
        this.imageErrors.cover = false
        const response = await courseService.uploadImage(file)
        console.log('上传响应:', response)
        if (response.success && response.data && response.data.url) {
          const imageUrl = response.data.url
          console.log('封面图片URL:', imageUrl)
          // 清除之前的错误状态和缓存的预签名URL
          this.imageErrors.cover = false
          this.presignedUrls.cover = null
          this.formData.coverImageUrl = imageUrl
          // watch会自动触发加载预签名URL
        } else {
          alert('图片上传失败')
        }
      } catch (error) {
        console.error('上传封面图片失败', error)
        alert('图片上传失败：' + (error.response?.data?.message || error.message))
      } finally {
        this.uploading.cover = false
      }
    },
    async handleBannerImageChange(event) {
      const file = event.target.files[0]
      if (!file) return
      
      // 验证文件类型
      if (!file.type.startsWith('image/')) {
        alert('请选择图片文件')
        return
      }
      
      // 验证文件大小（10MB）
      if (file.size > 10 * 1024 * 1024) {
        alert('图片大小不能超过10MB')
        return
      }
      
      try {
        this.uploading.banner = true
        this.imageErrors.banner = false
        const response = await courseService.uploadImage(file)
        console.log('上传响应:', response)
        if (response.success && response.data && response.data.url) {
          const imageUrl = response.data.url
          console.log('轮播图URL:', imageUrl)
          this.formData.bannerImageUrl = imageUrl
          this.imageErrors.banner = false
          this.presignedUrls.banner = null
        } else {
          alert('图片上传失败')
        }
      } catch (error) {
        console.error('上传轮播图失败', error)
        alert('图片上传失败：' + (error.response?.data?.message || error.message))
      } finally {
        this.uploading.banner = false
      }
    },
    async handleDetailImageChange(event) {
      const file = event.target.files[0]
      if (!file) return
      
      // 验证文件类型
      if (!file.type.startsWith('image/')) {
        alert('请选择图片文件')
        return
      }
      
      // 验证文件大小（10MB）
      if (file.size > 10 * 1024 * 1024) {
        alert('图片大小不能超过10MB')
        return
      }
      
      try {
        this.uploading.detail = true
        this.imageErrors.detail = false
        const response = await courseService.uploadImage(file)
        console.log('上传响应:', response)
        if (response.success && response.data && response.data.url) {
          const imageUrl = response.data.url
          console.log('详情图URL:', imageUrl)
          this.formData.detailImageUrl = imageUrl
          this.imageErrors.detail = false
          this.presignedUrls.detail = null
        } else {
          alert('图片上传失败')
        }
      } catch (error) {
        console.error('上传详情图失败', error)
        alert('图片上传失败：' + (error.response?.data?.message || error.message))
      } finally {
        this.uploading.detail = false
      }
    },
    clearCoverImage() {
      this.formData.coverImageUrl = ''
      this.imageErrors.cover = false
      this.presignedUrls.cover = null
      if (this.$refs.coverImageInput) {
        this.$refs.coverImageInput.value = ''
      }
    },
    clearBannerImage() {
      this.formData.bannerImageUrl = ''
      this.imageErrors.banner = false
      this.presignedUrls.banner = null
      if (this.$refs.bannerImageInput) {
        this.$refs.bannerImageInput.value = ''
      }
    },
    clearDetailImage() {
      this.formData.detailImageUrl = ''
      this.imageErrors.detail = false
      this.presignedUrls.detail = null
      if (this.$refs.detailImageInput) {
        this.$refs.detailImageInput.value = ''
      }
    },
    handleImageError(type) {
      console.error(`图片加载失败 (${type}):`, this.formData[`${type}ImageUrl`])
      this.imageErrors[type] = true
    },
    handleImageLoad(type) {
      console.log(`图片加载成功 (${type}):`, this.formData[`${type}ImageUrl`])
      this.imageErrors[type] = false
    },
    isOssPrivateUrl(url) {
      if (!url) return false
      // 检查是否是OSS私有bucket的URL
      return url.includes('todayacademy-courses-files.oss-cn-heyuan.aliyuncs.com')
    },
    async loadPresignedUrl(type, imageUrl) {
      if (!imageUrl || !this.isOssPrivateUrl(imageUrl)) {
        return
      }
      
      try {
        const response = await courseService.getPresignedUrl(imageUrl)
        if (response.success && response.data && response.data.url) {
          this.presignedUrls[type] = response.data.url
          console.log(`预签名URL加载成功 (${type}):`, response.data.url)
        }
      } catch (error) {
        console.error(`获取预签名URL失败 (${type}):`, error)
        // 如果获取失败，保持原始URL，让浏览器尝试加载（可能会失败）
      }
    },
    async loadTags() {
      try {
        const response = await tagService.getAllTags()
        if (response.success) {
          this.availableTags = response.data || []
        }
      } catch (error) {
        console.error('加载标签列表失败', error)
      }
    },
    toggleTagDropdown() {
      if (this.originalStatus === 'ARCHIVED') return
      this.showTagDropdown = !this.showTagDropdown
    },
    toggleTagSelection(tagId) {
      if (this.originalStatus === 'ARCHIVED') return
      const index = this.formData.tagIds.indexOf(tagId)
      if (index > -1) {
        // 如果已选中，则移除
        this.formData.tagIds.splice(index, 1)
      } else {
        // 如果未选中，则添加
        this.formData.tagIds.push(tagId)
      }
    },
    removeTag(tagId) {
      if (this.originalStatus === 'ARCHIVED') return
      const index = this.formData.tagIds.indexOf(tagId)
      if (index > -1) {
        this.formData.tagIds.splice(index, 1)
      }
    },
    isTagSelected(tagId) {
      return this.formData.tagIds.includes(tagId)
    },
    getTagNameById(tagId) {
      const tag = this.availableTags.find(t => t.id === tagId)
      return tag ? tag.name : `标签${tagId}`
    },
    handleClickOutside(event) {
      // 检查点击是否在标签选择器外部
      const tagSelector = event.target.closest('.tag-selector-wrapper')
      if (!tagSelector && this.showTagDropdown) {
        this.showTagDropdown = false
      }
    },
    // 付款账号相关操作
    addPaymentAccount() {
      if (this.originalStatus === 'ARCHIVED') return
      this.formData.paymentAccounts.push({
        id: null,
        bankName: '',
        bankAccount: '',
        accountHolder: '',
        displayOrder: this.formData.paymentAccounts.length
      })
    },
    removePaymentAccount(index) {
      if (this.originalStatus === 'ARCHIVED') return
      this.formData.paymentAccounts.splice(index, 1)
    },
    moveAccountUp(index) {
      if (this.originalStatus === 'ARCHIVED') return
      if (index <= 0) return
      const accounts = this.formData.paymentAccounts
      const temp = accounts[index]
      accounts[index] = accounts[index - 1]
      accounts[index - 1] = temp
    },
    moveAccountDown(index) {
      if (this.originalStatus === 'ARCHIVED') return
      if (index >= this.formData.paymentAccounts.length - 1) return
      const accounts = this.formData.paymentAccounts
      const temp = accounts[index]
      accounts[index] = accounts[index + 1]
      accounts[index + 1] = temp
    },
    goToEnrollmentApproval(courseId) {
      // 跳转到报名审批页面，并传递课程ID作为查询参数
      this.$router.push({
        name: 'EnrollmentApproval',
        query: { courseId: courseId }
      })
    }
  }
}
</script>

<style scoped>
.course-management {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.subtitle {
  color: #6b7280;
  margin-top: 0.25rem;
}

.filter-panel {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  background: #fff;
  padding: 1rem;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-item label {
  font-size: 0.85rem;
  color: #4b5563;
}

.filter-item input,
.filter-item select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-sizing: border-box;
  height: 2.5rem;
  font-size: 0.875rem;
  line-height: 1.5;
}

.filter-actions {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
}

.table-wrapper {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead th {
  background: #f9fafb;
  text-align: left;
  padding: 0.75rem 1rem;
  color: #6b7280;
  font-weight: 600;
  font-size: 0.9rem;
}

tbody td {
  padding: 0.8rem 1rem;
  border-top: 1px solid #f1f5f9;
  vertical-align: top;
  text-align: left;
}

.title-cell {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.course-title {
  font-weight: 600;
  color: #111827;
}

.course-title.clickable {
  cursor: pointer;
  color: #2563eb;
  transition: color 0.2s ease;
}

.course-title.clickable:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

.course-summary {
  font-size: 0.85rem;
  color: #6b7280;
}

.time-cell {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.time-separator {
  color: #9ca3af;
  font-size: 0.8rem;
}

.tags-cell {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  max-width: 200px;
}

.tag-chip-small {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.4rem;
  background: #eff6ff;
  color: #2563eb;
  border-radius: 4px;
  font-size: 0.75rem;
  line-height: 1.2;
  white-space: nowrap;
}

.status-tag {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: none;
}

.status-tag.enrolling {
  background: rgba(59, 130, 246, 0.1);
  color: #2563eb;
}

.status-tag.ongoing {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
}

.status-tag.completed {
  background: rgba(99, 102, 241, 0.1);
  color: #4c1d95;
}

.status-tag.archived {
  background: rgba(107, 114, 128, 0.12);
  color: #4b5563;
}

.status-tag.draft {
  background: rgba(209, 213, 219, 0.4);
  color: #374151;
}

.status-tag.published {
  background: rgba(14, 165, 233, 0.12);
  color: #0369a1;
}

.status-tag.scheduled.draft {
  box-shadow: inset 0 0 0 1px rgba(245, 158, 11, 0.45);
}

.status-tag.scheduled.published {
  box-shadow: inset 0 0 0 1px rgba(14, 165, 233, 0.5);
}

.status-tag .schedule-hint {
  display: block;
  font-size: 0.72rem;
  font-weight: 600;
  margin-top: 0.2rem;
  color: #b45309;
}

.schedule-publish-field .schedule-publish-controls {
  margin-top: 0.35rem;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.schedule-publish-field .checkbox-inline {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  line-height: 1.5;
}

.schedule-publish-field .checkbox-inline .schedule-publish-checkbox {
  flex-shrink: 0;
  width: 1.35rem;
  height: 1.35rem;
  min-width: 1.35rem;
  min-height: 1.35rem;
  margin-top: 0.15rem;
  cursor: pointer;
  accent-color: #2563eb;
}

.schedule-publish-field .checkbox-inline .schedule-publish-caption {
  font-size: 0.9375rem;
  user-select: text;
}

.schedule-publish-field .schedule-datetime-split {
  margin-top: 0.1rem;
}

.schedule-publish-field .schedule-datetime-split-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
}

.schedule-publish-field .schedule-subfield {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.schedule-publish-field .schedule-subfield-label {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.form-grid .schedule-publish-field input[type='date'],
.form-grid .schedule-publish-field input[type='time'] {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.95rem;
  background: #f9fafb;
  min-height: 38px;
}

.primary-button,
.ghost-button,
.link-button,
.danger-link {
  cursor: pointer;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  transition: all 0.2s ease;
}

.primary-button {
  background: linear-gradient(135deg, #2563eb, #3b82f6);
  color: #fff;
  padding: 0.55rem 1.2rem;
  box-shadow: 0 10px 20px rgba(37, 99, 235, 0.25);
}

.primary-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ghost-button {
  background: #f3f4f6;
  color: #374151;
  padding: 0.55rem 1.2rem;
}

.link-button {
  background: transparent;
  color: #2563eb;
  padding: 0.25rem 0.5rem;
}

.danger-link {
  background: transparent;
  color: #ef4444;
  padding: 0.25rem 0.5rem;
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid #f1f5f9;
  background: #fafafa;
}

.empty-state {
  text-align: center;
  color: #6b7280;
  padding: 2rem;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 90;
  padding: 1rem;
}

.modal {
  background: #fff;
  padding: 1.5rem;
  border-radius: 12px;
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.2);
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.form-grid label,
.form-grid .schedule-publish-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #374151;
}

.form-grid input,
.form-grid select,
.form-grid textarea {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.95rem;
  background: #f9fafb;
}

.form-grid select {
  height: 38px;
  line-height: 1.4;
}

.form-grid textarea {
  resize: vertical;
}

  .form-grid .full-width {
    grid-column: 1 / -1;
  }

  .tag-selector-wrapper {
    position: relative;
    width: 100%;
  }

  .tag-selector-wrapper.disabled {
    opacity: 0.6;
    pointer-events: none;
  }

  .tag-input-container {
    min-height: 40px;
    padding: 0.5rem 2.5rem 0.5rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    background: #f9fafb;
    cursor: pointer;
    display: flex;
    align-items: center;
    transition: all 0.2s ease;
  }

  .tag-input-container:hover:not(.disabled) {
    border-color: #2563eb;
    background: #fff;
  }

  .tag-input-container.disabled {
    cursor: not-allowed;
  }

  .selected-tags-container {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    flex: 1;
    min-height: 24px;
  }

  .tag-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.5rem;
    background: #2563eb;
    color: white;
    border-radius: 4px;
    font-size: 0.85rem;
    line-height: 1.4;
  }

  .tag-remove {
    cursor: pointer;
    font-size: 1.2rem;
    line-height: 1;
    font-weight: bold;
    opacity: 0.8;
    transition: opacity 0.2s;
  }

  .tag-remove:hover {
    opacity: 1;
  }

  .tag-placeholder {
    color: #9ca3af;
    font-size: 0.95rem;
  }

  .tag-dropdown-arrow {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #6b7280;
    font-size: 0.75rem;
    transition: transform 0.2s ease;
    pointer-events: none;
  }

  .tag-dropdown-arrow.open {
    transform: translateY(-50%) rotate(180deg);
  }

  .tag-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin-top: 0.25rem;
    max-height: 200px;
    overflow-y: auto;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 100;
  }

  .tag-dropdown-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    cursor: pointer;
    transition: background-color 0.2s ease;
    border-bottom: 1px solid #f3f4f6;
  }

  .tag-dropdown-item:last-child {
    border-bottom: none;
  }

  .tag-dropdown-item:hover {
    background-color: #f9fafb;
  }

  .tag-dropdown-item.selected {
    background-color: #eff6ff;
    color: #2563eb;
  }

  .tag-dropdown-item.selected .tag-name {
    font-weight: 600;
  }

  .tag-name {
    font-size: 0.95rem;
    color: #374151;
  }

  .tag-dropdown-item.selected .tag-name {
    color: #2563eb;
  }

  .tag-check {
    color: #2563eb;
    font-weight: bold;
    font-size: 1rem;
  }

  .tag-dropdown-empty {
    padding: 1rem;
    text-align: center;
    color: #9ca3af;
    font-size: 0.9rem;
  }

  .no-tags-hint {
    color: #6b7280;
    font-size: 0.9rem;
    padding: 0.5rem;
    font-style: italic;
  }

/* 付款账号列表样式 */
.payment-accounts-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.payment-account-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.account-fields {
  display: flex;
  flex: 1;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.account-input {
  flex: 1;
  min-width: 120px;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9rem;
  background: white;
}

.account-input-number {
  min-width: 160px;
}

.account-actions {
  display: flex;
  gap: 0.25rem;
}

.icon-button {
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.icon-button:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.icon-button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.icon-button.remove-account {
  color: #ef4444;
  border-color: #fecaca;
}

.icon-button.remove-account:hover:not(:disabled) {
  background: #fef2f2;
  border-color: #ef4444;
}

.no-accounts-hint {
  color: #6b7280;
  font-size: 0.9rem;
  padding: 1rem;
  text-align: center;
  background: #f9fafb;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
}

.add-account-btn {
  align-self: flex-start;
}

.free-course-hint {
  padding: 0.75rem 1rem;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  color: #166534;
}

.free-course-referral-hint {
  grid-column: 1 / -1;
  margin: 0 0 0.25rem;
  font-size: 0.82rem;
  color: #6b7280;
}

.payment-accounts-hint {
  color: #6b7280;
  font-size: 0.8rem;
  padding: 0.5rem 0;
  font-style: italic;
}

.recommender-opinion-lock-hint {
  display: block;
  margin-top: 0.35rem;
  font-size: 0.8rem;
  color: #6b7280;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
}

.image-upload-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.image-preview-container {
  width: 100%;
  min-height: 200px;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f9fafb;
  overflow: hidden;
  position: relative;
}

.image-preview {
  max-width: 100%;
  max-height: 400px;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
}

.image-placeholder {
  color: #9ca3af;
  font-size: 0.9rem;
  padding: 2rem;
}

.image-error {
  color: #ef4444;
  font-size: 0.85rem;
  padding: 0.5rem;
  text-align: center;
}

.image-upload-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.upload-status {
  color: #2563eb;
  font-size: 0.85rem;
  font-weight: 500;
}

/* 课程内容板块编辑器（对齐康养项目管理） */
.section-fieldset {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 0;
}
.section-fieldset legend {
  font-weight: 600;
  font-size: 1rem;
  color: #374151;
  padding: 0 8px;
}
.cs-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.cs-hint {
  font-size: 0.8125rem;
  color: #6b7280;
}
.cs-add-btns {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.cs-add-btn {
  font-size: 0.8125rem;
  padding: 0.3rem 0.7rem;
  white-space: nowrap;
}
.cs-empty {
  text-align: center;
  color: #9ca3af;
  font-size: 0.875rem;
  padding: 16px 0;
}
.cs-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 10px;
  background: #fff;
}
.cs-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.cs-title-input {
  flex: 1;
  font-weight: 600;
  font-size: 0.9375rem;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 4px 8px;
}
.cs-type-badge {
  font-size: 0.75rem;
  background: #eff6ff;
  color: #1d4ed8;
  padding: 2px 10px;
  border-radius: 9999px;
  white-space: nowrap;
}
.cs-card-actions {
  display: flex;
  gap: 2px;
  margin-left: auto;
}
.cs-action-btn {
  background: none;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85rem;
  padding: 2px 8px;
  color: #374151;
  line-height: 1.2;
}
.cs-action-btn:hover {
  background: #f3f4f6;
}
.cs-remove-btn {
  color: #ef4444;
  border-color: #fecaca;
}
.cs-remove-btn:hover {
  background: #fef2f2;
}
.cs-card-body {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.cs-item-row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.cs-item-num {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #1e40af;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
}
.cs-item-bullet {
  font-size: 1.2rem;
  color: #374151;
  flex-shrink: 0;
  width: 24px;
  text-align: center;
}
.cs-item-title {
  width: 140px;
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.85rem;
  flex-shrink: 0;
}
.cs-item-desc {
  flex: 1;
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.85rem;
  min-width: 0;
}
.cs-textarea {
  width: 100%;
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  line-height: 1.6;
  resize: vertical;
  box-sizing: border-box;
}
.cs-highlight-body {
  background: #fffbeb;
  border-radius: 6px;
  padding: 8px;
  border-left: 3px solid #f59e0b;
}

.cs-card-body.cs-video-fields {
  gap: 12px;
}
.cs-field-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.cs-field-block > span {
  font-size: 0.8rem;
  font-weight: 500;
  color: #374151;
}
.cs-field-block .cs-title-input {
  width: 100%;
  box-sizing: border-box;
}
.cs-field-block.cs-checkbox-row {
  flex-direction: row;
  align-items: flex-start;
  gap: 10px;
}
.cs-field-block.cs-checkbox-row input[type='checkbox'] {
  margin-top: 4px;
  flex-shrink: 0;
}
.cs-field-block.cs-checkbox-row > span {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  line-height: 1.45;
}
.cs-inline-hint {
  font-size: 0.8rem;
  color: #6b7280;
  margin: 0 0 10px;
  line-height: 1.55;
}
.cs-inline-hint a {
  color: #2563eb;
}
.cs-poster-upload-row {
  display: flex;
  align-items: stretch;
  gap: 8px;
  flex-wrap: wrap;
}
.cs-poster-input {
  flex: 1;
  min-width: 180px;
}
.cs-poster-upload-btn {
  flex-shrink: 0;
  align-self: center;
  white-space: nowrap;
}
.cs-hidden-file-input {
  display: none;
}

@media (max-width: 768px) {
  .filter-panel {
    grid-template-columns: 1fr;
  }

  .pagination {
    flex-direction: column;
    gap: 0.5rem;
  }

  .modal {
    max-width: 95vw;
  }
  
  .image-preview-container {
    min-height: 150px;
  }
  
  .image-preview {
    max-height: 300px;
  }
}
</style>

